import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  IMAGE_PREFIX,
  TIME_PERIOD_KEY,
  INITIAL_CREATOR_FILTERS,
} from "./constants";
import { triggerGa } from "./gaUtils";

export const createFilterHandler =
  (setter, key, options = {}) =>
  (value) => {
    setter((prev) => ({ ...prev, [key]: value }));
    if (options.gaConfig) {
      const { category, action, label, label2, valueMapper, page, screenName } =
        options.gaConfig;
      let eventValue;
      if (valueMapper) {
        if (typeof valueMapper === "function") {
          eventValue = valueMapper(value);
        } else if (typeof valueMapper === "object") {
          eventValue = valueMapper[value] || 0;
        }
      } else if (Array.isArray(value)) {
        eventValue = value.length;
      } else if (typeof value === "boolean") {
        eventValue = value ? 1 : 0;
      } else {
        eventValue = 1;
      }
      triggerGa({
        category: category || "Analytics",
        action: action || "filter_change",
        label: label || key,
        label2: label2 || "",
        value: eventValue || 0,
        page: page || "unknown_page",
        screenName: screenName || "unknown_screen",
      });
    }
  };

// Generic state updater for date ranges
export const createDateRangeHandler =
  (setter, options = {}) =>
  (range) => {
    setter((prev) => ({
      ...prev,
      startDate: range.startDate,
      endDate: range.endDate,
    }));
    if (options.gaConfig) {
      const { category, action, label, label2, page, screenName } =
        options.gaConfig;
      const eventValue = range.endDate.diff(range.startDate, "day") + 1;
      console.log(" daterange called?", eventValue);
      triggerGa({
        category: category || "Analytics",
        action: action || "filter_change",
        label: label,
        label2: label2 || "",
        value: eventValue || 0,
        page: page || "unknown_page",
        screenName: screenName || "unknown_screen",
      });
    }
  };

// Generic pagination handler
export const createPaginationHandler = (setter) => (value) => {
  setter((prev) => ({
    ...prev,
    page: value,
  }));
};

export const createMultiTablePaginationHandler =
  (setter) => (value, pageKey) => {
    setter((prev) => ({
      ...prev,
      [pageKey]: value,
    }));
  };

// Generic rows per page handler
export const createRowsPerPageHandler = (setter) => (event) => {
  setter((prev) => ({
    ...prev,
    page: 0,
    rowsPerPage: parseInt(event.target.value, 10),
  }));
};
export const createMultiTableRowsPerPageHandler =
  (setter) => (event, pageKey, rowsPerPageKey) => {
    setter((prev) => ({
      ...prev,
      [pageKey]: 0,
      [rowsPerPageKey]: parseInt(event.target.value, 10),
    }));
  };

// Generic sort handler
export const createSortHandler = (setter) => (id, direction) => {
  setter((prev) => ({
    ...prev,
    sortBy: id,
    sortDirection: direction,
  }));
};

export const createMultiTableSortHandler =
  (setter) => (id, direction, idKey, directionKey) => {
    setter((prev) => ({
      ...prev,
      [idKey]: id,
      [directionKey]: direction,
    }));
  };

// Helper function to safely extract campaign name from selectedCampaign object
export const extractCampaignName = (selectedCampaign) => {
  if (!selectedCampaign) return null;
  return selectedCampaign.id || selectedCampaign.value;
};

export const sanitizeQueryParams = (filters) => {
  // for dates, send dayjs objects only
  const today = dayjs();
  dayjs.extend(isoWeek);

  let startDate, endDate, previousStartDate, previousEndDate;
  let errorMessage = null;
  if (filters.brands && filters.brands.length === 0) {
    errorMessage = "Please select at least one brand.";
  } else if (filters.selectedFields && filters.selectedFields.length === 0) {
    errorMessage = "Please select at least one field.";
  } else if (filters.selectedMetrics && filters.selectedMetrics.length === 0) {
    errorMessage = "Please select at least one metric.";
  }

  if (filters.dateType === TIME_PERIOD_KEY) {
    switch (filters.toDatePeriod?.id) {
      case "mtd":
        startDate = today.startOf("month");
        previousStartDate = today.subtract(1, "month").startOf("month");
        endDate = today;
        previousEndDate = today.subtract(1, "month").endOf("month");
        break;
      case "wtd":
        startDate = today.startOf("isoWeek");
        previousStartDate = today.subtract(1, "week").startOf("isoWeek");
        endDate = today;
        previousEndDate = today.subtract(1, "week").endOf("isoWeek");
        break;
      case "today":
        startDate = today;
        previousStartDate = today.subtract(1, "day");
        endDate = today;
        previousEndDate = today.subtract(1, "day");
        break;
      default:
        errorMessage = "Invalid period";
    }
  } else {
    if (
      (filters.startDate || filters.endDate) &&
      (!dayjs.isDayjs(filters.startDate) ||
        !filters.startDate.isValid() ||
        !dayjs.isDayjs(filters.endDate) ||
        !filters.endDate.isValid())
    ) {
      errorMessage = "Invalid date";
    }
    startDate = filters.startDate !== undefined ? filters.startDate : undefined;
    endDate = filters.endDate !== undefined ? filters.endDate : undefined;
  }

  return {
    startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
    endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
    brands: filters.brands
      ? filters.brands.map((b) => b.value).join(",")
      : undefined,
    fields: filters.selectedFields
      ? filters.selectedFields.map((f) => f.backendKey).join(",")
      : undefined,
    metrics: filters.selectedMetrics
      ? filters.selectedMetrics.map((b) => b.backendKey).join(",")
      : undefined,
    previousStartDate: previousStartDate
      ? previousStartDate.format("YYYY-MM-DD")
      : undefined,
    previousEndDate: previousEndDate
      ? previousEndDate.format("YYYY-MM-DD")
      : undefined,
    error: errorMessage,
    campaignName: extractCampaignName(filters.selectedCampaign),
    selectedBrandIds: filters.selectedBrandIds
      ? filters.selectedBrandIds.join(",")
      : undefined,
  };
};

export function formatNumberWithCommas(value) {
  if (value == null) {
    return "N/A";
  }
  const numericValue = Number(value);
  if (isNaN(numericValue)) {
    return value;
  }
  return numericValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

export function formatNumberWithCommasButNoDecimalPoints(value) {
  if (value == null) {
    return "N/A";
  }
  const numericValue = Number(value);
  if (isNaN(numericValue)) {
    return value;
  }
  return numericValue.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

export function formatIndianCurrency(value) {
  if (value == null) return "-";
  const n = Number(value);
  if (isNaN(n)) return value;
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)}Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(2)}K`;
  return `₹${n.toFixed(2)}`;
}

export const normalizeUrl = (url) => {
  if (!url) return null;

  try {
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }
    const parsedUrl = new URL(url);
    parsedUrl.protocol = "https:";
    parsedUrl.pathname = parsedUrl.pathname.replace(/\/+/g, "/");
    return parsedUrl.toString().replace(/\/$/, "");
  } catch (error) {
    return null;
  }
};

export const clearStorageExcept = (keysToKeep) => {
  // Clear localStorage
  Object.keys(localStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });
  // Clear sessionStorage
  Object.keys(sessionStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) {
      sessionStorage.removeItem(key);
    }
  });
};

// this simpleFormatter also adds commas to the number acc to indian system
// export const simpleFormatter = (value, unit) => {
//     if (value === null || value === undefined) return '-';
//
//     const number = Number(value);
//     if (isNaN(number)) return '-';
//
//     return `${number.toLocaleString('en-IN', {
//         minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
//         maximumFractionDigits: 2
//     })}${unit ? ` ${unit}` : ''}`;
// };

// this simpleFormatter only rounds off the decimal
export const decimalFormatter = (value, unit) => {
  if (value === null || value === undefined) return "-";
  const number = Number(value);
  if (isNaN(number)) return "-";
  let formattedValue;
  if (Number.isInteger(number)) {
    formattedValue = number.toString();
  } else {
    formattedValue = number.toFixed(2).replace(/\.?0+$/, "");
  }
  return `${formattedValue}${unit ? ` ${unit}` : ""}`;
};

export const numberFormatter = (value, unit) => {
  if (value === null || value === undefined) return "-";
  const number = Number(value);
  if (isNaN(number)) return "-";
  // Always show integer, no decimals
  const formattedValue = Math.round(number).toString();
  return `${formattedValue}${unit ? ` ${unit}` : ""}`;
};

export const jsonFormatter = (value, unit) => {
  if (typeof value !== "string") return numberFormatter(value, unit);

  try {
    const parsed = JSON.parse(value);
    return Object.entries(parsed)
      .map(([key, val]) => {
        const formatted = numberFormatter(val, unit);
        return `${key}:  ${formatted}  `;
      })
      .join(" ; ");
  } catch {
    return numberFormatter(value, unit);
  }
};

export const formatterFactory = (column) => (value) => {
  if (
    "formatterOverride" in column &&
    Object.is(column.formatterOverride, value)
  ) {
    return value ?? "-";
  }
  switch (column.formatterType) {
    case "currency":
      return numberFormatter(value, column.unit || "₹");
    case "json":
      return jsonFormatter(value, column.unit);
    case "number":
      return numberFormatter(value, "");
    case "decimal":
      return decimalFormatter(value, "");
    default:
      return value !== null ? String(value) : "-";
  }
};

export const formatNumberKMB = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "N/A";
  if (num === 0) return "0";
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
};

export const formatNumberLakhsCrores = (num) => {
  if (num === null || num === undefined || isNaN(num) || num === 0)
    return "N/A";

  const number = Number(num);

  if (number >= 10000000) {
    // Crores (1 crore = 10 million)
    const value = (number / 10000000).toFixed(1);
    return parseFloat(value) + "Cr";
  } else if (number >= 100000) {
    // Lakhs (1 lakh = 100 thousand)
    const value = (number / 100000).toFixed(1);
    return parseFloat(value) + "L";
  } else if (number >= 1000) {
    // Thousands
    const value = (number / 1000).toFixed(1);
    return parseFloat(value) + "K";
  }
  const value = number.toFixed(1);
  return parseFloat(value)?.toString() || "N/A";
};

export const getAssetUrl = (assetUrl) => {
  return IMAGE_PREFIX + assetUrl;
};

export const formatNumberLakhs = (num, noDecimal = true) => {
  if (num === null || num === undefined || isNaN(num) || num === 0)
    return "N/A";

  const number = Number(num);
  const options = noDecimal
    ? { maximumFractionDigits: 0, minimumFractionDigits: 0 }
    : {};
  const formatted = number.toLocaleString("en-IN", options);

  return formatted;
};

export const formatRelativeTime = (date) => {
  if (!date) return "-";

  // optional normalization for "+0000" -> "+00:00" (safer parsing)
  const normalized =
    typeof date === "string"
      ? date.replace(/([+-]\d{2})(\d{2})$/, "$1:$2")
      : date;

  const now = dayjs();
  const targetDate = dayjs(normalized);
  if (!targetDate.isValid()) return "-";

  const diffInSeconds = now.diff(targetDate, "second");
  const diffInMinutes = now.diff(targetDate, "minute");
  const diffInHours = now.diff(targetDate, "hour");
  const diffInDays = now.diff(targetDate, "day");

  if (diffInSeconds < 60) return "just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  if (diffInDays < 7)
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;

  // 7..29 days => weeks (so 28 days => "4 weeks ago", not "0 months ago")
  if (diffInDays < 30) {
    const weeks = Math.max(1, Math.floor(diffInDays / 7));
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }

  // 30..364 days => months
  if (diffInDays < 365) {
    const months = Math.max(1, Math.floor(diffInDays / 30));
    return `${months} month${months === 1 ? "" : "s"} ago`;
  }

  // 365+ days => years
  const years = Math.max(1, Math.floor(diffInDays / 365));
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

export const formatDate = (date, format = "DD/MM/YYYY") => {
  if (!date) return "-";
  return dayjs(date).format(format);
};

export const hasFiltersChanged = (currentFilters) => {
  const initialFilters = INITIAL_CREATOR_FILTERS;

  const compareArrays = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return true;
    return arr1.some((item, index) => {
      if (typeof item === "object" && item !== null) {
        return JSON.stringify(item) !== JSON.stringify(arr2[index]);
      }
      return item !== arr2[index];
    });
  };

  const compareObjects = (obj1, obj2, excludeKeys = []) => {
    // Handle null or undefined objects
    if (obj1 === null && obj2 === null) return false;
    if (obj1 === null || obj2 === null) return true;
    if (obj1 === undefined && obj2 === undefined) return false;
    if (obj1 === undefined || obj2 === undefined) return true;

    const keys1 = Object.keys(obj1).filter((key) => !excludeKeys.includes(key));
    const keys2 = Object.keys(obj2).filter((key) => !excludeKeys.includes(key));

    if (keys1.length !== keys2.length) return true;

    return keys1.some((key) => {
      const val1 = obj1[key];
      const val2 = obj2[key];

      if (Array.isArray(val1)) {
        return compareArrays(val1, val2);
      } else if (typeof val1 === "object" && val1 !== null) {
        return compareObjects(val1, val2);
      } else {
        return val1 !== val2;
      }
    });
  };

  return compareObjects(currentFilters, initialFilters, ["pagination"]);
};

export const hasFiltersChangedExcludingSort = (currentFilters) => {
  const initialFilters = INITIAL_CREATOR_FILTERS;

  const compareArrays = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return true;
    return arr1.some((item, index) => {
      if (typeof item === "object" && item !== null) {
        return JSON.stringify(item) !== JSON.stringify(arr2[index]);
      }
      return item !== arr2[index];
    });
  };

  const compareObjects = (obj1, obj2, excludeKeys = []) => {
    // Handle null or undefined objects
    if (obj1 === null && obj2 === null) return false;
    if (obj1 === null || obj2 === null) return true;
    if (obj1 === undefined && obj2 === undefined) return false;
    if (obj1 === undefined || obj2 === undefined) return true;

    const keys1 = Object.keys(obj1).filter((key) => !excludeKeys.includes(key));
    const keys2 = Object.keys(obj2).filter((key) => !excludeKeys.includes(key));

    if (keys1.length !== keys2.length) return true;

    return keys1.some((key) => {
      const val1 = obj1[key];
      const val2 = obj2[key];

      if (Array.isArray(val1)) {
        return compareArrays(val1, val2);
      } else if (typeof val1 === "object" && val1 !== null) {
        return compareObjects(val1, val2);
      } else {
        return val1 !== val2;
      }
    });
  };

  return compareObjects(currentFilters, initialFilters, [
    "pagination",
    "sort_by",
  ]);
};
